import React from 'react'

import { FormProvider, useForm } from 'react-hook-form'
import { BSecure } from '@sga/shopify-payments-types'

import {
  Banner,
  Button,
  Card,
  Form,
  FormLayout,
  Heading,
  Link,
  Stack,
  TextContainer,
  TextStyle,
} from '@shopify/polaris'

import { createApi } from '../api'
import { segment } from '../util/createSegmentPicker'
import { getTranslation } from '../util/getTranslation'
import { useAuth } from './AuthContext'
import { FormTextField } from './FormTextField'

type TProps = {
  onSuccess(): void
}

BSecureCredentialsForm.segment = segment('bsecure')

const registrationFormUrl = 'https://builder.bsecure.pk/signup'

export function BSecureCredentialsForm({ onSuccess }: TProps) {
  const config = useAuth()
  const {
    segmentCredentials,
    segmentConfig: { sandboxCredentials },
  } = config.account as BSecure.TSegmentAccount
  const form = useForm<BSecure.TSegmentCredentials>({
    reValidateMode: 'onChange',
  })

  const errorMessageRef = React.useRef<null | string>(null)
  const [testMode, setTestMode] = React.useState(true)
  const [status, setStatus] = React.useState<'init' | 'wrong-env' | 'failed' | 'done'>('init')

  React.useEffect(() => {
    if (testMode) {
      form.reset({
        clientId: sandboxCredentials?.clientId,
        clientSecret: sandboxCredentials?.clientSecret,
        storeId: sandboxCredentials?.storeId,
      })
    } else {
      form.reset({
        clientId: segmentCredentials.clientId,
        clientSecret: segmentCredentials.clientSecret,
        storeId: segmentCredentials.storeId,
      })
    }
  }, [testMode, form, segmentCredentials, sandboxCredentials])

  const onSubmit = form.handleSubmit(async (values) => {
    setStatus('init')
    const api = createApi()
    const { success, code, errorMessage } = await api.verifyCredentials(values, testMode)
    if (success) {
      setStatus('done')
      if (testMode) {
        setTestMode(false)
      } else {
        onSuccess()
      }
    } else if (code === 'wrong-env') {
      setStatus('wrong-env')
    } else {
      errorMessageRef.current = errorMessage ?? null
      setStatus('failed')
    }
  })

  return (
    <Card sectioned title={getTranslation('bsecure.credentials_title')}>
      <Stack vertical>
        <TextContainer>
          <p>
            {getTranslation('bsecure.credentials_description')}
          </p>
          <p>
            <TextStyle variation="strong">
              {testMode
                ? getTranslation('bsecure.test_credentials_instruction')
                : getTranslation('bsecure.production_credentials_instruction')}
            </TextStyle>
          </p>
        </TextContainer>
        <FormProvider {...form}>
          <Form onSubmit={onSubmit}>
            <FormLayout>
              <FormTextField name="clientId" label={getTranslation('fields.client_id')} />
              <FormTextField name="clientSecret" label={getTranslation('fields.client_secret')} />
              <FormTextField name="storeId" label={getTranslation('fields.store_id')} />
              <Stack distribution="trailing">
                <Button primary submit disabled={form.formState.isSubmitting}>
                  {getTranslation('bsecure.verify_credentials', { mode: testMode ? 'test' : 'production' })}
                </Button>
              </Stack>
              {status === 'wrong-env' && (
                <Banner status="critical">
                  <Heading>{getTranslation('bsecure.wrong_credentials')}</Heading>
                  <p>
                    {testMode
                      ? getTranslation('bsecure.wrong_test_credentials')
                      : getTranslation('bsecure.wrong_production_credentials')}
                  </p>
                </Banner>
              )}
              {status === 'failed' && (
                <Banner status="critical">
                  <Heading>{getTranslation('bsecure.verification_failed')}</Heading>
                  <p>
                    <TextStyle variation="negative">
                      {errorMessageRef.current || getTranslation('bsecure.unexpected_error')}
                    </TextStyle>
                  </p>
                </Banner>
              )}
            </FormLayout>
          </Form>
        </FormProvider>
        <TextContainer>
          <Heading>{getTranslation('bsecure.no_account_title')}</Heading>
          <p>
            {getTranslation('bsecure.no_account_description')}
          </p>
        </TextContainer>
      </Stack>
    </Card>
  )
}
